import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './addnewAccessories.css';

const AddNewAccessories = () => {
  const [newAccessory, setNewAccessory] = useState('');
  const [accessories, setAccessories] = useState({
    moreAccessories: [],
    filterAccessories: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('more');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Dialog states
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showUpdateConfirmDialog, setShowUpdateConfirmDialog] = useState(false);
  const [updateData, setUpdateData] = useState(null);
  const [showAddConfirmDialog, setShowAddConfirmDialog] = useState(false);

  useEffect(() => {
    const fetchAccessories = async () => {
      try {
        const response = await axios.get(
          'https://car.amar.co.jp/api/dashboard/product/get-all-accessories',
          {
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en'
            }
          }
        );
        
        // Validate and ensure arrays
        const data = response.data || {};
        setAccessories({
          moreAccessories: Array.isArray(data.moreAccessories) ? data.moreAccessories : [],
          filterAccessories: Array.isArray(data.filterAccessories) ? data.filterAccessories : [],
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching accessories:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchAccessories();
  }, []);

  // Add new accessory flow
  const handleAddClick = () => {
    if (!newAccessory.trim()) {
      setError('Accessory name cannot be empty');
      return;
    }
    setShowAddConfirmDialog(true);
  };

  const handleConfirmAdd = async () => {
    setShowAddConfirmDialog(false);

    try {
      const response = await axios.post(
        `https://car.amar.co.jp/api/dashboard/car-more-accessories/save?moreAccessoriesName=${encodeURIComponent(newAccessory.trim())}`,
        {},
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      if (response.data && response.data.accessory) {
        setAccessories(prev => ({
          ...prev,
          moreAccessories: [...prev.moreAccessories, response.data.accessory]
        }));
        setNewAccessory('');
        setError(null);
      }
    } catch (err) {
      console.error('Error adding accessory:', err);
      setError(`Failed to add accessory: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleCancelAdd = () => {
    setShowAddConfirmDialog(false);
  };

  const handleCancel = () => {
    setNewAccessory('');
  };

  // Edit/update flow
  const handleEdit = (id, currentValue) => {
    setEditingId(id);
    setEditValue(currentValue);
  };

  const handleSaveEdit = (type) => {
    if (!editValue.trim()) {
      setError('Accessory name cannot be empty');
      return;
    }
    setUpdateData({ type, id: editingId, newValue: editValue.trim() });
    setShowUpdateConfirmDialog(true);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleCancelUpdate = () => {
    setShowUpdateConfirmDialog(false);
    setUpdateData(null);
  };

  const handleConfirmUpdate = async () => {
    if (!updateData) return;

    const { type, id, newValue } = updateData;

    try {
      setUpdateLoading(true);
      setShowUpdateConfirmDialog(false);

      const apiType = type === 'more' ? 'MORE' : 'FILTER';

      const response = await axios.put(
        `https://car.amar.co.jp/api/dashboard/product/update-accessories?type=${apiType}&id=${id}&name=${encodeURIComponent(newValue)}`,
        {},
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      if (response.data) {
        setAccessories(prevAccessories => {
          if (type === 'more') {
            return {
              ...prevAccessories,
              moreAccessories: prevAccessories.moreAccessories.map(item =>
                item.carMoreAccessoriesId === id
                  ? { ...item, carMoreAccessories: newValue }
                  : item
              )
            };
          } else {
            return {
              ...prevAccessories,
              filterAccessories: prevAccessories.filterAccessories.map(item =>
                item.carFilterAccessoriesId === id
                  ? { ...item, carFilterAccessories: newValue }
                  : item
              )
            };
          }
        });

        setEditingId(null);
        setEditValue('');
        setError(null);
      }
    } catch (err) {
      console.error('Error updating accessory:', err);
      setError(`Failed to update accessory: ${err.response?.data?.message || err.message}`);
    } finally {
      setUpdateLoading(false);
      setUpdateData(null);
    }
  };

  // Delete flow
  const handleDeleteClick = (id, type, name) => {
    setItemToDelete({ id, type, name });
    setShowDeleteConfirmDialog(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmDialog(false);
    setItemToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    const { id, type } = itemToDelete;

    try {
      setDeleteLoading(id);
      setShowDeleteConfirmDialog(false);

      const response = await axios.delete(
        `https://car.amar.co.jp/api/dashboard/product/delete-accessories?type=${type}&ids=${id}`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      if (response.data && response.data.deletedIds && response.data.deletedIds.includes(id)) {
        setAccessories(prevAccessories => {
          if (type === 'more') {
            return {
              ...prevAccessories,
              moreAccessories: prevAccessories.moreAccessories.filter(
                item => item.carMoreAccessoriesId !== id
              )
            };
          } else {
            return {
              ...prevAccessories,
              filterAccessories: prevAccessories.filterAccessories.filter(
                item => item.carFilterAccessoriesId !== id
              )
            };
          }
        });
      }
    } catch (err) {
      console.error('Error deleting accessory:', err);
      setError(`Failed to delete accessory: ${err.response?.data?.message || err.message}`);
    } finally {
      setDeleteLoading(null);
      setItemToDelete(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  return (
    <div className="accessories-container">
      <h1 className="title">Manage Accessories</h1>

      <div className="add-section">
        <h2 className="section-title">Add New Accessory</h2>
        <div className="input-group">
          <input
            type="text"
            value={newAccessory}
            onChange={(e) => setNewAccessory(e.target.value)}
            placeholder="Enter accessory name"
            className="accessory-input"
          />
          <div className="button-group">
            <button
              className="add-button"
              onClick={handleAddClick}
            >
              Add
            </button>
            <button className="cancel-button" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'more' ? 'active' : ''}`}
          onClick={() => setActiveTab('more')}
        >
          More Accessories ({accessories.moreAccessories.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'filter' ? 'active' : ''}`}
          onClick={() => setActiveTab('filter')}
        >
          Filter Accessories ({accessories.filterAccessories.length})
        </button>
      </div>

      <div className="accessories-list">
        {activeTab === 'more' ? (
          <>
            <h2 className="section-title">More Accessories</h2>
            {accessories.moreAccessories.length === 0 ? (
              <div className="empty-state">No accessories found</div>
            ) : (
              <div className="accessories-grid">
                {accessories.moreAccessories.map((item) => (
                  <div key={item.carMoreAccessoriesId} className="accessory-card">
                    {editingId === item.carMoreAccessoriesId ? (
                      <div className="edit-container-add-new-accessories">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="edit-input-add-new-accessories"
                          disabled={updateLoading}
                        />
                        <div className="edit-buttons-add-new-accessories">
                          <button
                            className="save-button"
                            onClick={() => handleSaveEdit('more')}
                            disabled={updateLoading}
                          >
                            {updateLoading ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="cancel-edit-button-add-new-accessories"
                            onClick={handleCancelEdit}
                            disabled={updateLoading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="accessory-name">{item.carMoreAccessories}</span>
                        <div className="action-buttons">
                          <button
                            className="edit-button-add-new-accessories"
                            onClick={() => handleEdit(item.carMoreAccessoriesId, item.carMoreAccessories)}
                          >
                            Edit
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteClick(item.carMoreAccessoriesId, 'more', item.carMoreAccessories)}
                            disabled={deleteLoading === item.carMoreAccessoriesId}
                          >
                            {deleteLoading === item.carMoreAccessoriesId ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <h2 className="section-title">Filter Accessories</h2>
            {accessories.filterAccessories.length === 0 ? (
              <div className="empty-state">No accessories found</div>
            ) : (
              <div className="accessories-grid">
                {accessories.filterAccessories.map((item) => (
                  <div key={item.carFilterAccessoriesId} className="accessory-card">
                    {editingId === item.carFilterAccessoriesId ? (
                      <div className="edit-container-add-new-accessories">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="edit-input-add-new-accessories"
                          disabled={updateLoading}
                        />
                        <div className="edit-buttons-add-new-accessories">
                          <button
                            className="save-button"
                            onClick={() => handleSaveEdit('filter')}
                            disabled={updateLoading}
                          >
                            {updateLoading ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            className="cancel-edit-button"
                            onClick={handleCancelEdit}
                            disabled={updateLoading}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="accessory-name">{item.carFilterAccessories}</span>
                        <div className="action-buttons">
                          {/* <button
                                                    className="edit-button-add-new-accessories"
                                                    onClick={() => handleEdit(item.carFilterAccessoriesId, item.carFilterAccessories)}
                                                >
                                                    Edit
                                                </button> */}
                          {/* <button
                                                    className="delete-button"
                                                    onClick={() => handleDeleteClick(item.carFilterAccessoriesId, 'filter', item.carFilterAccessories)}
                                                    disabled={deleteLoading === item.carFilterAccessoriesId}
                                                >
                                                    {deleteLoading === item.carFilterAccessoriesId ? 'Deleting...' : 'Delete'}
                                                </button> */}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Confirmation Dialog */}
      {showAddConfirmDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Confirm Add</h3>
            </div>
            <div className="confirmation-body">
              <p
                style={{
                  color: '#b71c1c',
                  backgroundColor: '#ffebee',
                  padding: '10px',
                  border: '1px solid #f44336',
                  borderRadius: '6px',
                  fontWeight: 'bold'
                }}>
                Are you sure you want to add "{newAccessory}"?</p>
            </div>
            <div className="confirmation-buttons">
              <button
                className="confirm-delete-button"
                onClick={handleConfirmAdd}
              >
                Confirm
              </button>
              <button
                className="cancel-delete-button"
                onClick={handleCancelAdd}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="confirmation-body">
              <p style={{
                color: '#b71c1c',
                backgroundColor: '#ffebee',
                padding: '10px',
                border: '1px solid #f44336',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                Warning: This accessory is already associated with multiple products. Deleting it will reflect across all those products.
              </p>
            </div>
            <div className="confirmation-buttons">
              <button
                className="confirm-delete-button"
                onClick={handleConfirmDelete}
              >
                Confirm
              </button>
              <button
                className="cancel-delete-button"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Confirmation Dialog */}
      {showUpdateConfirmDialog && (
        <div className="confirmation-overlay">
          <div className="confirmation-dialog">
            <div className="confirmation-header">
              <h3>Confirm Update</h3>
            </div>
            <div className="confirmation-body">
              <p style={{
                color: '#b71c1c',
                backgroundColor: '#ffebee',
                padding: '10px',
                border: '1px solid #f44336',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                Warning: This accessory is already associated with multiple products. Updating it will reflect across all those products.
              </p>
            </div>
            <div className="confirmation-buttons">
              <button
                className="confirm-update-button"
                onClick={handleConfirmUpdate}
                disabled={updateLoading}
              >
                {updateLoading ? 'Updating...' : 'Confirm'}
              </button>
              <button
                className="cancel-update-button"
                onClick={handleCancelUpdate}
                disabled={updateLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNewAccessories;